<script setup lang="ts">
import { ref } from 'vue'

/**
 * Section header used across the collection sidebar — "Activities & Routes (N)" /
 * "Points of Interest (N)" / "Photos (N)". Consolidates the header shell repeated ~3× per
 * render branch in WorkspaceToolbar (and the mobile equivalents). Matches production:
 * `text-[16px]` title + count, h-[38px] row, hover row, no leading icon.
 *
 * Slots: `leading` (inline after the title — e.g. the Sync-to-map toggle) · `actions` (trailing
 * buttons). `rootEl` is exposed so callers can scroll/measure the header.
 */
interface Props {
  title: string
  count?: number
  clickable?: boolean
}
const props = withDefaults(defineProps<Props>(), { count: undefined, clickable: false })

const emit = defineEmits<{ headerClick: [] }>()

const rootEl = ref<HTMLElement | null>(null)
defineExpose({ rootEl })
</script>

<template>
  <div
    ref="rootEl"
    class="bg-white px-3 transition-colors"
    :class="props.clickable ? 'cursor-pointer hover:bg-gray-200' : ''"
    @click="props.clickable ? emit('headerClick') : undefined"
  >
    <div class="flex h-[38px] items-center justify-between">
      <div class="flex min-w-0 items-center gap-1.5">
        <h3 class="shrink-0 text-[16px] font-semibold text-gray-900">
          {{ props.title
          }}<template v-if="props.count !== undefined"> ({{ props.count }})</template>
        </h3>
        <slot name="leading" />
      </div>
      <div class="flex shrink-0 items-center gap-1">
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>
